import React from "react";
import { HelpCircle, Shield, Flag, ListChecks, Users, Scale } from "lucide-react";
import { stamp } from "./CallPlayer.jsx";

// The Substance half of the measured layer — ECHO's Group II.
//
// These were being computed on every call and thrown away: the pipeline
// returned them, Django stored them in `measures`, and nothing rendered them
// because the band-gauge component only knows how to draw numbers that sit on
// a scale. Most of Group II is not that shape — "was an objection raised", "is
// there a hard next step", "which qualification criteria were covered" are
// countable facts and lists, not positions on a track.
//
// The division of labour matters and is easy to blur: this block answers
// *what happened* — an objection was raised at 1:03, no next step was agreed.
// Whether the answer to that objection was any good stays a rubric judgement,
// and lives in the scored parameters above. Detection here, judgement there.
//
// The one rule that governs almost every row below: **a list that was never
// configured returns null, and null is not zero.** "0 competitors mentioned"
// reads as "they didn't discuss the competition". The truth is "nobody has told
// us who our competitors are". Those are opposite conclusions, and rendering
// the first when we mean the second is exactly the confident-garbage failure
// this whole design exists to refuse.

const muted = { color: "var(--c-500)" };

function Row({ icon: Icon, label, value, detail, tone = "var(--c-800)" }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "18px minmax(0,1fr) auto", gap: 9,
                  alignItems: "baseline", padding: "6px 0" }}>
      <Icon size={13} style={{ color: "var(--c-500)", alignSelf: "center" }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12.5, color: "var(--c-800)" }}>{label}</div>
        {detail && (
          <div style={{ fontSize: 11.5, ...muted, marginTop: 2, lineHeight: 1.5 }}>{detail}</div>
        )}
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: tone, whiteSpace: "nowrap" }}>
        {value}
      </div>
    </div>
  );
}

const NEXT_STEP = {
  hard: { text: "A date was agreed", tone: "var(--green-600)" },
  soft: { text: "Agreed in principle, no date", tone: "var(--blue-600)" },
  none: { text: "None agreed on the call", tone: "var(--red-600, #c0392b)" },
};

export default function ContentSignals({ measures }) {
  if (!measures || typeof measures !== "object") return null;
  const m = measures;
  const rows = [];

  // ── Questions ──────────────────────────────────────────────────────────────
  if (typeof m.open_question_ratio === "number") {
    const open = m.open_questions ?? null;
    const closed = m.closed_questions ?? null;
    rows.push(
      <Row key="q" icon={HelpCircle} label="Questions you asked"
           value={`${Math.round(m.open_question_ratio * 100)}% open`}
           detail={open != null && closed != null
             ? `${open} open, ${closed} closed. Open questions are the ones that make them explain.`
             : null} />,
    );
  }

  // ── Objections ─────────────────────────────────────────────────────────────
  if (typeof m.objections_raised === "number") {
    const raised = m.objections_raised;
    const addressed = m.objections_addressed ?? 0;
    rows.push(
      <Row key="obj" icon={Shield} label="Pushback"
           value={raised === 0 ? "none" : `${addressed} of ${raised} answered`}
           tone={raised === 0 ? "var(--c-500)" : addressed < raised
             ? "var(--red-600, #c0392b)" : "var(--green-600)"}
           // Silence beats invention: no objection is a real, useful sentence
           // and is not the same as handling one badly.
           detail={raised === 0
             ? "Nobody pushed back on this call, so there is nothing to judge here."
             : (m.objection_moments || []).map((o) => o.heard).filter(Boolean).slice(0, 1)[0] || null} />,
    );
  }

  // ── Next step ──────────────────────────────────────────────────────────────
  if (m.next_step_clarity) {
    const spec = NEXT_STEP[m.next_step_clarity] || NEXT_STEP.none;
    const at = stamp(m.next_step_at_seconds);
    rows.push(
      <Row key="next" icon={Flag} label="Next step" value={spec.text} tone={spec.tone}
           // The in-call cause of `follow_through`, which is a CRM outcome and
           // deliberately unscoreable from a recording. This is the observable
           // half of the same thing, available immediately.
           detail={m.next_step_heard
             ? `“${m.next_step_heard}”${at ? ` · ${at}` : ""}`
             : "Whether a date was agreed is the in-call cause of whether the follow-up happens."} />,
    );
  }

  // ── Qualification ──────────────────────────────────────────────────────────
  if (typeof m.qualification_coverage === "number") {
    const covered = m.qualification_covered || [];
    const missing = m.qualification_missing || [];
    const pretty = (k) => k.replace(/_/g, " ");
    rows.push(
      <Row key="qual" icon={ListChecks} label="Qualification covered"
           value={`${covered.length} of ${covered.length + missing.length}`}
           tone={m.qualification_coverage >= 0.75 ? "var(--green-600)"
             : m.qualification_coverage >= 0.4 ? "var(--blue-600)" : "var(--red-600, #c0392b)"}
           detail={missing.length
             ? `Didn't come up: ${missing.map(pretty).join(", ")}.`
             : "Everything on the checklist came up."} />,
    );
  }

  // ── Competitors ────────────────────────────────────────────────────────────
  // null means "no list configured", which must never render as zero.
  if (m.competitor_mentions === null || typeof m.competitor_mentions === "number") {
    const unconfigured = m.competitor_mentions === null;
    rows.push(
      <Row key="comp" icon={Users} label="Competitors named"
           value={unconfigured ? "not tracked" : String(m.competitor_mentions)}
           tone={unconfigured ? "var(--c-500)" : "var(--c-800)"}
           detail={unconfigured
             ? "No competitor list is configured, so this is unknown rather than none."
             : (m.competitor_moments || []).map((c) => c.name).filter(Boolean).join(", ") || null} />,
    );
  }

  // ── Value vs feature ───────────────────────────────────────────────────────
  if (typeof m.value_vs_feature_ratio === "number"
      && (m.feature_terms != null || m.value_terms != null)) {
    const f = m.feature_terms ?? 0;
    const v = m.value_terms ?? 0;
    rows.push(
      <Row key="vf" icon={Scale} label="Value vs features"
           value={f + v === 0 ? "neither" : `${v} value, ${f} feature`}
           tone={v > f ? "var(--green-600)" : f > 0 ? "var(--blue-600)" : "var(--c-500)"}
           detail={f > v
             ? "You described what it does more than what it changes for them."
             : null} />,
    );
  }

  if (!rows.length) return null;

  return (
    <div>
      {rows}
      <p style={{ fontSize: 10.5, ...muted, margin: "8px 0 0", lineHeight: 1.5 }}>
        Counted from the transcript, not judged. Whether an answer was any good is the
        scored part above.
      </p>
    </div>
  );
}
